import { uuidv7 } from '../ids';
import { deriveMaster } from './kdf';
import {
  aad,
  CryptoError,
  decryptBlob,
  deriveSubKey,
  encryptBlob,
  fromBase64,
  generateKeyPair,
  KEY_BYTES,
  newKdfParams,
  openSealed,
  randomKey,
  sealFor,
  sodiumReady,
  toBase64,
  wipe,
  type KdfParams,
  type KeyPair,
} from './sodium';

/** Paramètres Argon2id tels que le serveur les stocke et les renvoie (`kdf_params`, JSON ; sel en base64). */
export interface ServerKdf {
  alg: string;
  salt: string;
  m: number;
  t: number;
  p: number;
}

export function kdfFromServer(json: unknown): KdfParams {
  const k = json as Partial<ServerKdf> | null;
  if (!k || k.alg !== 'argon2id' || typeof k.salt !== 'string' || typeof k.m !== 'number' || typeof k.t !== 'number') {
    throw new CryptoError('paramètres de dérivation invalides');
  }
  if (k.p !== 1) throw new CryptoError('paramètres de dérivation non pris en charge sur le web (parallélisme)');
  const salt = fromBase64(k.salt);
  if (salt.length !== 16) throw new CryptoError('paramètres de dérivation non pris en charge sur le web (sel)');
  return { alg: 'argon2id', salt, m: k.m, t: k.t, p: 1 };
}

export const kdfToServer = (p: KdfParams): ServerKdf => ({ alg: p.alg, salt: toBase64(p.salt), m: p.m, t: p.t, p: p.p });

/** Clés déverrouillées d'un utilisateur. En mémoire uniquement ; `wipe()` à la déconnexion. */
export class Keyring {
  constructor(
    readonly accountKey: Uint8Array,
    readonly keyPair: KeyPair,
    private readonly spaces: Map<string, Uint8Array>,
  ) {}

  spaceKey(spaceId: string): Uint8Array {
    const key = this.spaces.get(spaceId);
    if (!key) throw new CryptoError('clé d’espace indisponible');
    return key;
  }

  get spaceIds(): string[] {
    return [...this.spaces.keys()];
  }

  wipe(): void {
    wipe(this.accountKey, this.keyPair.privateKey, ...this.spaces.values());
    this.spaces.clear();
  }
}

export interface UserKeysDto {
  publicKey: string;
  encPrivateKey: string;
  wrappedAccountKey: string;
  wrappedAccountKeyRecovery?: string | null;
}

export interface SpaceKeyDto {
  id: string;
  wrappedSpaceKey: string;
}

export interface Credentials {
  /** `auth_key` envoyée au serveur (jamais le mot de passe ni la KEK). */
  authKey: Uint8Array;
  kek: Uint8Array;
}

/** Mot de passe maître → KEK (gardée) et `auth_key` (envoyée). */
export async function deriveCredentials(password: string, params: KdfParams): Promise<Credentials> {
  await sodiumReady();
  const master = await deriveMaster(password, params);
  const credentials = { kek: deriveSubKey(master, 'kek'), authKey: deriveSubKey(master, 'authKey') };
  wipe(master);
  return credentials;
}

export interface RegistrationMaterial {
  /** Corps de `POST /auth/register` (sans l'e-mail ni l'appareil, ajoutés par l'appelant). */
  body: {
    authKey: string;
    kdfParams: ServerKdf;
    publicKey: string;
    encPrivateKey: string;
    wrappedAccountKey: string;
    wrappedAccountKeyRecovery: string;
    space: SpaceKeyDto;
  };
  /** Recovery Key : à afficher une seule fois à l'utilisateur, jamais envoyée. */
  recoveryKey: Uint8Array;
  keyring: Keyring;
  spaceId: string;
}

/** Fabrique toutes les clés d'un nouveau compte (chaîne de clés de Docs/crypto/crypto.md §5 et §6). */
export async function createAccount(password: string, kdf: KdfParams = newKdfParams()): Promise<RegistrationMaterial> {
  await sodiumReady();
  const { kek, authKey } = await deriveCredentials(password, kdf);
  const accountKey = randomKey();
  const keyPair = generateKeyPair();
  const recoveryKey = randomKey();
  const spaceKey = randomKey();
  const spaceId = uuidv7();

  const body = {
    authKey: toBase64(authKey),
    kdfParams: kdfToServer(kdf),
    publicKey: toBase64(keyPair.publicKey),
    encPrivateKey: toBase64(encryptBlob(accountKey, keyPair.privateKey, aad('private-key'))),
    wrappedAccountKey: toBase64(encryptBlob(kek, accountKey, aad('account-key'))),
    wrappedAccountKeyRecovery: toBase64(encryptBlob(recoveryKey, accountKey, aad('account-key-recovery'))),
    space: { id: spaceId, wrappedSpaceKey: toBase64(sealFor(keyPair.publicKey, spaceKey)) },
  };
  wipe(kek, authKey);
  return { body, recoveryKey, keyring: new Keyring(accountKey, keyPair, new Map([[spaceId, spaceKey]])), spaceId };
}

/** Déverrouille les clés reçues à la connexion avec la KEK. Un mauvais mot de passe échoue ici, sans détail. */
export function unlockKeyring(kek: Uint8Array, user: UserKeysDto, spaces: readonly SpaceKeyDto[]): Keyring {
  const accountKey = decryptBlob(kek, fromBase64(user.wrappedAccountKey), aad('account-key'));
  if (accountKey.length !== KEY_BYTES) throw new CryptoError('clé de compte invalide');
  const privateKey = decryptBlob(accountKey, fromBase64(user.encPrivateKey), aad('private-key'));
  const keyPair: KeyPair = { publicKey: fromBase64(user.publicKey), privateKey };
  const keys = new Map<string, Uint8Array>();
  for (const space of spaces) {
    keys.set(space.id, openSealed(keyPair, fromBase64(space.wrappedSpaceKey)));
  }
  return new Keyring(accountKey, keyPair, keys);
}

/**
 * Vrai si ce mot de passe maître ouvre la clé de compte (`wrapped_account_key`). Aucune clé n'est conservée : la clé de
 * compte déchiffrée est mise à zéro aussitôt. Sert à déverrouiller un service sans refaire une connexion.
 */
export async function verifyMasterPassword(password: string, params: KdfParams, wrappedAccountKey: string): Promise<boolean> {
  const { kek, authKey } = await deriveCredentials(password, params);
  wipe(authKey);
  try {
    const accountKey = decryptBlob(kek, fromBase64(wrappedAccountKey), aad('account-key'));
    wipe(accountKey);
    return true;
  } catch (error) {
    if (error instanceof CryptoError) return false;
    throw error;
  } finally {
    wipe(kek);
  }
}

/** Retrouve la clé de compte avec la Recovery Key (réinitialisation du mot de passe : parcours à venir). */
export function recoverAccountKey(recoveryKey: Uint8Array, wrappedAccountKeyRecovery: string): Uint8Array {
  return decryptBlob(recoveryKey, fromBase64(wrappedAccountKeyRecovery), aad('account-key-recovery'));
}

const BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/** Recovery Key lisible : Base32 (A–Z, 2–7) par groupes de 4 caractères. */
export function formatRecoveryKey(key: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = '';
  for (const byte of key) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += BASE32[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += BASE32[(value << (5 - bits)) & 31];
  return out.match(/.{1,4}/g)?.join('-') ?? '';
}

export function parseRecoveryKey(text: string): Uint8Array {
  const clean = text.toUpperCase().replace(/[^A-Z2-7]/g, '');
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of clean) {
    value = (value << 5) | BASE32.indexOf(ch);
    bits += 5;
    if (bits >= 8) {
      bytes.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  if (bytes.length !== KEY_BYTES) throw new CryptoError('Recovery Key invalide');
  return Uint8Array.from(bytes);
}
